import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { useAppSelector } from '@/store/hooks';
import { useI18nText } from '@/lib/utils/i18nText';
import { getBlockingHeavyRefreshCount, subscribeHeavyRefresh } from '@/lib/utils/indexedDbCache';

const BARS = 12;

/**
 * True while some caller is *blocked* on a heavy-cache network fetch (cold cache,
 * forceRefresh, or an expired entry read without stale-while-revalidate). Background
 * revalidations deliberately don't count — this annotation must only claim "the cache
 * is being refreshed" when a loader is genuinely waiting on it.
 */
function useHeavyRefreshActive(): boolean {
  const count = useSyncExternalStore(
    subscribeHeavyRefresh,
    getBlockingHeavyRefreshCount,
    getBlockingHeavyRefreshCount,
  );
  return count > 0;
}

function SpinnerDots({ elapsed, message }: { elapsed: number; message?: React.ReactNode }) {
  return (
    <div className="page-spinner-block">
      <div className="page-spinner">
        {Array.from({ length: BARS }, (_, i) => (
          <div
            key={i}
            className="page-spinner-dot"
            style={{
              transform: `rotate(${i * 30}deg)`,
              animationDelay: `${-((BARS - i) / BARS) * 1.2}s`,
            }}
          />
        ))}
        <span className="page-spinner-elapsed">{elapsed}s</span>
      </div>
      {message && <div className="page-spinner-message">{message}</div>}
    </div>
  );
}

/**
 * Localized two-line message shown while a loader is blocked on a heavy cache — which,
 * with stale-while-revalidate, only happens when there is nothing usable cached (first
 * login, after "Refresh now", or an entry older than the stale bound). The copy says so
 * instead of "refreshing", since nothing is being refreshed behind the user's back.
 */
function useHeavyRefreshMessage(active: boolean): React.ReactNode {
  const { t } = useI18nText();
  if (!active) return undefined;
  return (
    <>
      <div>
        {t('Loading fresh data...', '데이터를 새로 불러오는 중입니다.')}
      </div>
      <div className="page-spinner-message__sub">
        {t(
          'Nothing usable is cached yet, so this waits for the server.',
          '사용 가능한 캐시가 없어 서버 응답을 기다리고 있습니다.',
        )}
      </div>
      <div>
        <i className="fa fa-database text-[40px] text-ggc-text-gray-light opacity-30 mt-6" aria-hidden="true"></i>
      </div>
    </>
  );
}

/** 전역 스피너 오버레이 — count > 0일 때 표시 */
export default function Spinner() {
  const { active } = useAppSelector((s) => s.spinner);
  const heavyRefreshing = useHeavyRefreshActive();
  const heavyMessage = useHeavyRefreshMessage(heavyRefreshing);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!active) return null;

  return (
    <div className="page-spinner-overlay">
      <SpinnerDots elapsed={elapsed} message={heavyMessage} />
    </div>
  );
}

/** 페이지 초기 로딩 시 사용하는 공유 컴포넌트 */
export function PageLoader() {
  const heavyRefreshing = useHeavyRefreshActive();
  const heavyMessage = useHeavyRefreshMessage(heavyRefreshing);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="page-spinner-wrapper">
      <SpinnerDots elapsed={elapsed} message={heavyMessage} />
    </div>
  );
}

/** 테이블 셀 등에서 사용하는 소형 인라인 스피너 */
export function InlineSpinner({ size = 16 }: { size?: number }) {
  return (
    <span
      className="inline-spinner"
      style={{ width: size, height: size }}
    />
  );
}
